import React from 'react';
import { createRoot } from 'react-dom/client';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { STATUS_CONFIG, SUB_STATUS_CONFIG } from '../types';
import { Activity, Clock, ShieldCheck, HeartPulse, Building, Calendar, User } from 'lucide-react';

// The hidden component we will capture
const BeautifulPatientReport = ({ patient, generatedAt }: { patient: any, generatedAt: string }) => {
    
    const cleanName = typeof patient.name === 'string' ? patient.name.replace(/,/g, ' ') : '';
    const cleanProc = typeof patient.procedureName === 'string' ? patient.procedureName.replace(/,/g, ' ') : patient.procedure || '';
    const cleanDoctor = typeof patient.doctorName === 'string' ? patient.doctorName.replace(/,/g, ' ') : patient.doctor || '';
    
    let admissionTimeStr = '---';
    if (patient.admissionTime) {
      try { admissionTimeStr = new Date(patient.admissionTime).toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' }); } catch(e){}
    }
    
    let dischargeTimeStr = '---';
    if (patient.dischargeTime) {
      try { dischargeTimeStr = new Date(patient.dischargeTime).toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' }); } catch(e){}
    }

    // Adapt to both Patient object (StaffDashboard) and Surgery object (HospitalDashboard)
    const patientId = patient.id;
    const room = patient.room || 'No asignada';
    
    // Unify logs (StaffDashboard uses patient.logs, HospitalDashboard uses surgery.stationBreakdown)
    const hasLogs = patient.logs && patient.logs.length > 0;
    const logs = hasLogs ? patient.logs : [];

    // Calculate station breakdown if logs exist, otherwise use pre-calculated (from HospitalDashboard)
    let breakdown = patient.stationBreakdown || [];
    if (hasLogs && breakdown.length === 0) {
        const sortedLogs = [...logs].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        const stations = ['WAITING', 'PREPARATION', 'ROOM_ENTRY', 'ANESTHESIA', 'SURGERY', 'RECOVERY'];
        const calcBreakdown: any[] = [];
        
        for (let i = 0; i < sortedLogs.length; i++) {
            const log = sortedLogs[i];
            if (!stations.includes(log.status)) continue;
            if (calcBreakdown.some(b => b.station === log.status)) continue;
            
            const startTime = new Date(log.timestamp).getTime();
            const nextDiffLog = sortedLogs.slice(i + 1).find(l => l.status !== log.status && stations.includes(l.status));
            
            let endTime;
            if (nextDiffLog) {
                endTime = new Date(nextDiffLog.timestamp).getTime();
            } else {
                const exitLog = sortedLogs.find(l => l.status === 'EXIT');
                if (exitLog) {
                    endTime = new Date(exitLog.timestamp).getTime();
                } else if (patient.dischargeTime) {
                    endTime = new Date(patient.dischargeTime).getTime();
                } else {
                    endTime = new Date().getTime();
                }
            }
            const durationMin = Math.round((endTime - startTime) / 60000);
            calcBreakdown.push({ station: log.status, durationMin });
        }
        
        stations.forEach(st => {
            if (!calcBreakdown.some(b => b.station === st)) calcBreakdown.push({ station: st, durationMin: null });
        });
        calcBreakdown.sort((a,b) => stations.indexOf(a.station) - stations.indexOf(b.station));
        breakdown = calcBreakdown;
    }

    return (
        <div className="w-[800px] min-h-[1130px] font-sans p-12 relative overflow-hidden" id="pdf-report-canvas" style={{ backgroundColor: '#F8FAFC', color: '#1E293B' }}>
            {/* Background design accents */}
            <div className="absolute top-0 left-0 w-full h-2" style={{ background: 'linear-gradient(to right, #34D399, #6366F1, #8B5CF6)' }} />

            {/* Header */}
            <div className="flex justify-between items-start mb-10 relative z-10">
                <div>
                    <h1 className="text-3xl font-black tracking-tight flex items-center gap-3" style={{ color: '#0F172A' }}>
                        HOSPITAL STATUS
                        <ShieldCheck className="w-8 h-8" style={{ color: '#6366F1' }} />
                    </h1>
                    <p className="font-medium mt-1" style={{ color: '#64748B' }}>Reporte Individual de Paciente</p>
                </div>
                <div className="text-right">
                    <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#94A3B8' }}>Generado</p>
                    <p className="text-sm font-medium" style={{ color: '#334155' }}>{generatedAt}</p>
                </div>
            </div>

            {/* Patient Info Card */}
            <div className="rounded-3xl p-8 border mb-10 relative z-10" style={{ backgroundColor: '#FFFFFF', borderColor: '#F1F5F9', boxShadow: '0 8px 30px rgba(0,0,0,0.04)' }}>
                <div className="grid grid-cols-2 gap-8">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <User className="w-4 h-4" style={{ color: '#94A3B8' }} />
                            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Identificación</p>
                        </div>
                        <p className="text-xs mb-1" style={{ color: '#64748B' }}>ID: <span className="font-mono font-bold" style={{ color: '#334155' }}>{patientId}</span></p>
                        <p className="text-lg font-bold" style={{ color: '#1E293B' }}>{cleanName || 'No Nombre'}</p>
                    </div>

                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <Activity className="w-4 h-4" style={{ color: '#94A3B8' }} />
                            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Detalles Médicos</p>
                        </div>
                        <p className="text-sm font-bold" style={{ color: '#1E293B' }}>{cleanProc || 'Sin Procedimiento'}</p>
                        <p className="text-sm mt-1" style={{ color: '#475569' }}>Dr. {cleanDoctor || 'No asignado'}</p>
                    </div>

                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <Building className="w-4 h-4" style={{ color: '#94A3B8' }} />
                            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Ubicación</p>
                        </div>
                        <p className="text-sm font-medium" style={{ color: '#1E293B' }}>{room}</p>
                    </div>

                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-4 h-4" style={{ color: '#94A3B8' }} />
                            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Tiempos Registrados</p>
                        </div>
                        <p className="text-xs mb-1" style={{ color: '#64748B' }}>Ingreso: <span className="font-semibold" style={{ color: '#334155' }}>{admissionTimeStr}</span></p>
                        {patient.dischargeTime && (
                           <p className="text-xs" style={{ color: '#64748B' }}>Alta: <span className="font-semibold" style={{ color: '#334155' }}>{dischargeTimeStr}</span></p>
                        )}
                    </div>
                </div>
            </div>

            {/* Station Summary (Tiempos Finales) */}
            <div className="relative z-10 mb-10">
                <div className="flex items-center gap-2 mb-6">
                    <Clock className="w-5 h-5" style={{ color: '#10B981' }} />
                    <h2 className="text-lg font-bold" style={{ color: '#1E293B' }}>Tiempos por Estación</h2>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                    {breakdown.filter((s:any) => s.durationMin !== null).map((s:any, idx:number) => {
                        const STATION_LABELS: Record<string, string> = {
                            WAITING: 'En Espera', PREPARATION: 'Preparación', ROOM_ENTRY: 'Ingreso a Qx',
                            ANESTHESIA: 'Anestesia', SURGERY: 'Cirugía', RECOVERY: 'Recuperación',
                        };
                        const STATION_COLORS: Record<string, string> = {
                            WAITING: '#94A3B8', PREPARATION: '#3B82F6', ROOM_ENTRY: '#8B5CF6',
                            ANESTHESIA: '#F59E0B', SURGERY: '#10B981', RECOVERY: '#6366F1',
                        };
                        return (
                            <div key={idx} className="flex items-center justify-between p-4 rounded-2xl border" style={{ backgroundColor: '#FFFFFF', borderColor: '#F1F5F9', boxShadow: '0 8px 30px rgba(0,0,0,0.02)' }}>
                                <div className="flex items-center gap-3">
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: STATION_COLORS[s.station] || '#94A3B8', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }} />
                                    <span className="text-sm font-bold" style={{ color: '#334155' }}>{STATION_LABELS[s.station] || s.station}</span>
                                </div>
                                <div className="text-right">
                                    <span className="text-xl font-black font-mono" style={{ color: '#1E293B' }}>{s.durationMin}</span>
                                    <span className="text-[10px] font-bold uppercase ml-1" style={{ color: '#94A3B8' }}>min</span>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>

            {/* Timeline Section */}
            {hasLogs && (
                <div className="relative z-10 mb-16">
                    <div className="flex items-center gap-2 mb-6">
                        <Activity className="w-5 h-5" style={{ color: '#6366F1' }} />
                        <h2 className="text-lg font-bold" style={{ color: '#1E293B' }}>Historial Detallado de Movimientos</h2>
                    </div>
                    
                    <div className="rounded-3xl p-6 md:p-8 border" style={{ backgroundColor: '#FFFFFF', borderColor: '#F1F5F9', boxShadow: '0 8px 30px rgba(0,0,0,0.04)' }}>
                        <div className="relative border-l-2 ml-4 space-y-8" style={{ borderColor: '#F1F5F9' }}>
                            {logs.map((log: any, idx: number) => {
                                const stLabel = STATUS_CONFIG[log.status as keyof typeof STATUS_CONFIG]?.label || log.status;
                                const subLabel = SUB_STATUS_CONFIG[log.subStatus as keyof typeof SUB_STATUS_CONFIG]?.label || log.subStatus;
                                const condLabel = typeof log.condition === 'string' ? log.condition.replace(/_/g, ' ') : '';
                                
                                let timeStr = '---';
                                if (log.timestamp) {
                                    try { timeStr = new Date(log.timestamp).toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'medium' }); } catch(e){}
                                }
                                
                                return (
                                    <div key={idx} className="relative pl-6">
                                        <div className="absolute -left-[9px] top-1.5 w-4 h-4 rounded-full border-4" style={{ backgroundColor: '#10B981', borderColor: '#FFFFFF', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }} />
                                        <div className="mb-1">
                                            <span className="text-sm font-bold" style={{ color: '#1E293B' }}>{stLabel}</span>
                                            {subLabel && subLabel !== stLabel && (
                                                <span className="text-sm font-medium ml-2 border-l pl-2" style={{ color: '#64748B', borderColor: '#E2E8F0' }}>
                                                    {subLabel}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs font-medium mb-1" style={{ color: '#94A3B8' }}>{timeStr}</p>
                                        {condLabel && (
                                            <span className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: '#F1F5F9', color: '#475569' }}>
                                                {condLabel}
                                            </span>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Footer */}
            <div className="absolute bottom-12 left-12 right-12 flex justify-between items-end border-t pt-6" style={{ borderColor: '#E2E8F0' }}>
                <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5" style={{ color: '#94A3B8' }}>
                        <HeartPulse className="w-3 h-3" /> Reporte Confidencial
                    </p>
                    <p className="text-[10px] max-w-[400px] mt-2 leading-relaxed" style={{ color: '#94A3B8' }}>
                        Este documento fue generado automáticamente por el sistema Hospital Status. La información contenida es de uso exclusivo médico.
                    </p>
                </div>
                <div className="text-right">
                    <p className="text-[10px] font-bold" style={{ color: '#CBD5E1' }}>ID SISTEMA: {patientId.slice(0, 8)}</p>
                </div>
            </div>
        </div>
    );
};

export const exportBeautifulPDF = async (patient: any) => {
    // 1. Create container
    const container = document.createElement('div');
    // Hide container far from viewport
    container.style.position = 'absolute';
    container.style.top = '-9999px';
    container.style.left = '-9999px';
    document.body.appendChild(container);

    const generatedAt = new Date().toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' });

    // 2. Render React Component into container
    const root = createRoot(container);
    root.render(<BeautifulPatientReport patient={patient} generatedAt={generatedAt} />);

    // 3. Wait a moment for fonts/SVG to render
    await new Promise(resolve => setTimeout(resolve, 800));

    try {
        // 4. Capture Canvas
        const printElement = container.querySelector('#pdf-report-canvas') as HTMLElement;
        if (!printElement) throw new Error("Canvas element not found");
        
        const canvas = await html2canvas(printElement, {
            scale: 2, // High resolution
            useCORS: true,
            logging: false,
            backgroundColor: '#F8FAFC' // Match slate-50
        });

        const imgData = canvas.toDataURL('image/png');

        // 5. Create PDF and calculate image dimensions
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        // A4 size is 210 x 297 mm
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
            position -= pageHeight;
            pdf.addPage();
            pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
            heightLeft -= pageHeight;
        }
        
        const cleanName = typeof patient.name === 'string' ? patient.name.replace(/,/g, ' ') : '';
        const filename = `Reporte_${cleanName || patient.id.slice(0,8)}.pdf`;
        pdf.save(filename);

    } catch (e) {
        console.error("Error generating PDF", e);
        alert(`Ocurrió un error al generar el PDF: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
        // 6. Cleanup
        root.unmount();
        document.body.removeChild(container);
    }
};
